/**
 * Reads an XLSX workbook in the browser for the table viewer: the values of each sheet, as text
 * (no formulas, no formats). A zip's entries are inflated with `DecompressionStream`.
 */

export type Sheet = { name: string; rows: string[][] };

/** How much of a sheet the viewer shows. */
export const MAX_ROWS = 2000;
export const MAX_COLUMNS = 200;

type ZipEntry = { method: number; compressedSize: number; offset: number };

/** The entries of a zip, from its central directory. */
function zipEntries(bytes: Uint8Array): Map<string, ZipEntry> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let at = bytes.length - 22; at >= Math.max(0, bytes.length - 65_557); at -= 1)
    if (view.getUint32(at, true) === 0x06054b50) {
      end = at;
      break;
    }
  if (end === -1) throw new Error('This file is not a workbook.');
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  const entries = new Map<string, ZipEntry>();
  const decoder = new TextDecoder();
  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(at, true) !== 0x02014b50) break;
    const method = view.getUint16(at + 10, true);
    const compressedSize = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const offset = view.getUint32(at + 42, true);
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength));
    entries.set(name, { method, compressedSize, offset });
    at += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function readEntry(bytes: Uint8Array, entry: ZipEntry): Promise<string> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const start =
    entry.offset +
    30 +
    view.getUint16(entry.offset + 26, true) +
    view.getUint16(entry.offset + 28, true);
  const data = bytes.slice(start, start + entry.compressedSize);
  if (entry.method === 0) return new TextDecoder().decode(data);
  if (entry.method !== 8)
    throw new Error('This workbook is compressed in a way that cannot be read.');
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return await new Response(stream).text();
}

/** A1 → its column index (0 for A). */
function columnOf(reference: string): number {
  const letters = /^[A-Z]+/.exec(reference)?.[0] ?? 'A';
  let column = 0;
  for (const letter of letters) column = column * 26 + (letter.charCodeAt(0) - 64);
  return column - 1;
}

const textOf = (element: Element) =>
  [...element.getElementsByTagName('t')].map((node) => node.textContent ?? '').join('');

/** The sheets of an XLSX file, each as rows of text, up to `MAX_ROWS` × `MAX_COLUMNS`. */
export async function readXlsx(bytes: Uint8Array): Promise<Sheet[]> {
  const entries = zipEntries(bytes);
  const read = async (name: string) => {
    const entry = entries.get(name);
    return entry === undefined ? null : await readEntry(bytes, entry);
  };
  const parse = (xml: string) => new DOMParser().parseFromString(xml, 'application/xml');
  const workbook = await read('xl/workbook.xml');
  if (workbook === null) throw new Error('This file is not a workbook.');
  const relations = parse((await read('xl/_rels/workbook.xml.rels')) ?? '<Relationships/>');
  const targets = new Map(
    [...relations.getElementsByTagName('Relationship')].map((relation) => [
      relation.getAttribute('Id') ?? '',
      relation.getAttribute('Target') ?? '',
    ]),
  );
  const strings = [
    ...parse((await read('xl/sharedStrings.xml')) ?? '<sst/>').getElementsByTagName('si'),
  ].map(textOf);
  const sheets: Sheet[] = [];
  for (const [index, sheet] of [...parse(workbook).getElementsByTagName('sheet')].entries()) {
    const id =
      sheet.getAttribute('r:id') ??
      sheet.getAttributeNS(
        'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
        'id',
      );
    const target = targets.get(id ?? '') ?? `worksheets/sheet${index + 1}.xml`;
    const path = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
    const xml = await read(path);
    const rows: string[][] = [];
    if (xml !== null)
      for (const row of [...parse(xml).getElementsByTagName('row')]) {
        const number = Number(row.getAttribute('r') ?? rows.length + 1) - 1;
        if (number >= MAX_ROWS) break;
        const values: string[] = [];
        for (const cell of [...row.getElementsByTagName('c')]) {
          const column = columnOf(cell.getAttribute('r') ?? '');
          if (column >= MAX_COLUMNS) continue;
          const type = cell.getAttribute('t');
          const value = cell.getElementsByTagName('v')[0]?.textContent ?? '';
          values[column] =
            type === 's'
              ? (strings[Number(value)] ?? '')
              : type === 'inlineStr'
                ? textOf(cell)
                : type === 'b'
                  ? value === '1'
                    ? 'TRUE'
                    : 'FALSE'
                  : value;
        }
        rows[number] = Array.from(values, (value) => value ?? '');
      }
    sheets.push({
      name: sheet.getAttribute('name') ?? `Sheet ${index + 1}`,
      rows: Array.from(rows, (row) => row ?? []),
    });
  }
  return sheets;
}
